/**
 * Free play on an instrument — /instrument/$instrumentId
 *
 * Includes "record and play": the child records what they play (notes with
 * timing) and plays it back later. The latest recording is persisted in
 * localStorage (without sync) and remains available after reloading.
 */

import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { createRoute, Link, useNavigate, useParams } from "@tanstack/solid-router";
import { ArrowLeft, Circle, Play, Square } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import {
  createRecorder,
  createRecordingStore,
  type InstrumentId,
  playRecording,
  type Recording,
  type RecordingPlayback,
  type SoundKey,
} from "@melodimals/game-core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { useAudio } from "@/providers/audio-provider";
import { InstrumentStage } from "@/features/instruments/instrument-stage";
import { LatencyOverlay } from "@/features/instruments/latency-overlay";
import { PlayErrorBoundary } from "@/components/play-error-boundary";
import { NotFoundState } from "@/components/ui/not-found-state";

const STAGE_BG: Record<InstrumentId, string> = {
  piano: "from-sky-100 to-sky-300",
  xylophone: "from-amber-100 to-orange-200",
  drums: "from-rose-100 to-rose-300",
};

function isInstrumentId(value: string): value is InstrumentId {
  return value === "piano" || value === "xylophone" || value === "drums";
}

function InstrumentStudio(props: { instrumentId: InstrumentId }) {
  const { t } = useI18n();
  const audio = useAudio();
  const instrument = (): InstrumentId => props.instrumentId;

  const store = createRecordingStore();
  const [recording, setRecording] = createSignal(false);
  const [playing, setPlaying] = createSignal(false);
  // A recording is stored → the play button is available.
  const [hasRecording, setHasRecording] = createSignal(false);
  const [highlighted, setHighlighted] = createSignal<SoundKey | null>(null);

  let recorder = createRecorder(instrument());
  let playback: RecordingPlayback | null = null;

  onMount(() => {
    setHasRecording(store.load() !== null);
  });

  const stopPlayback = () => {
    playback?.stop();
    playback = null;
    setPlaying(false);
    setHighlighted(null);
  };

  onCleanup(stopPlayback);

  const startRecording = () => {
    stopPlayback();
    recorder = createRecorder(instrument());
    recorder.start();
    setRecording(true);
  };

  const stopRecording = () => {
    const result = recorder.stop();
    setRecording(false);
    if (result) {
      store.save(result);
      setHasRecording(true);
    }
  };

  const startPlayback = () => {
    if (recording()) {
      return;
    }
    const saved: Recording | null = store.load();
    if (!saved) {
      return;
    }
    stopPlayback();
    setPlaying(true);
    playback = playRecording(audio.engine, saved, {
      onNote: (note) => {
        // Flash the playing key (visual feedback; clears on advance).
        setHighlighted(note.key);
      },
      onFinished: () => {
        setPlaying(false);
        setHighlighted(null);
      },
    });
  };

  /** In recording mode, every key touched is captured with its timing. */
  const handleStageNote = (key: SoundKey) => {
    if (recording()) {
      recorder.capture(key);
    }
  };

  return (
    <main class={`kid-stage-screen screen-enter bg-gradient-to-b p-3 ${STAGE_BG[instrument()]}`}>
      <LatencyOverlay />
      <header class="flex items-center gap-3 pb-2">
        <Link
          to="/"
          class="press flex h-12 w-12 items-center justify-center rounded-full bg-white/80 shadow"
          aria-label={t(msg`Back`)}
        >
          <ArrowLeft class="h-6 w-6 text-zinc-700" />
        </Link>

        <div class="ml-auto flex items-center gap-3">
          <Show
            when={recording()}
            fallback={
              <button
                type="button"
                onPointerDown={startRecording}
                disabled={playing()}
                class="press flex h-12 w-12 items-center justify-center rounded-full bg-white shadow disabled:opacity-40"
                aria-label={t(msg`Record`)}
                data-testid="record-button"
              >
                <Circle class="h-6 w-6 fill-rose-500 text-rose-600" />
              </button>
            }
          >
            <button
              type="button"
              onPointerDown={stopRecording}
              class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-500 shadow animate-pulse"
              aria-label={t(msg`Stop recording`)}
              data-testid="stop-recording-button"
            >
              <Square class="h-5 w-5 fill-white text-white" />
            </button>
          </Show>

          <Show
            when={playing()}
            fallback={
              <button
                type="button"
                onPointerDown={startPlayback}
                disabled={recording() || !hasRecording()}
                class="press flex h-12 w-12 items-center justify-center rounded-full bg-white shadow disabled:opacity-40"
                aria-label={t(msg`Play recording`)}
                data-testid="play-recording-button"
              >
                <Play class="h-6 w-6 text-emerald-700" />
              </button>
            }
          >
            <button
              type="button"
              onPointerDown={stopPlayback}
              class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 shadow"
              aria-label={t(msg`Stop`)}
              data-testid="stop-playback-button"
            >
              <Square class="h-5 w-5 fill-white text-white" />
            </button>
          </Show>
        </div>
      </header>
      <div class="min-h-0 flex-1">
        <PlayErrorBoundary scope={`instrument:${instrument()}`}>
          <InstrumentStage
            instrument={instrument()}
            highlightedKey={highlighted()}
            onNotePlayed={handleStageNote}
          />
        </PlayErrorBoundary>
      </div>
    </main>
  );
}

/**
 * Validates `$instrumentId` against the known instrument set. An unknown id
 * (broken link / manually edited URL) → a playful "not found" state with a CTA
 * back home, instead of silently falling back to the piano. The actual studio
 * mounts only with a valid id (via `<Show keyed>`, which also remounts it when
 * the instrument changes).
 */
function InstrumentPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ from: "/instrument/$instrumentId" });
  const validId = (): InstrumentId | null => {
    const id = params().instrumentId;
    return isInstrumentId(id) ? id : null;
  };

  return (
    <Show
      when={validId()}
      keyed
      fallback={
        <main class="kid-screen items-center justify-center bg-background p-6">
          <NotFoundState
            emoji="🎹"
            title={t(msg`We can't find that instrument`)}
            description={t(msg`This instrument isn't here. Let's go back and pick one!`)}
            primaryAction={{
              label: t(msg`Go home`),
              onClick: () => {
                void navigate({ to: "/" });
              },
            }}
          />
        </main>
      }
    >
      {(id) => <InstrumentStudio instrumentId={id} />}
    </Show>
  );
}

export const instrumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/instrument/$instrumentId",
  component: InstrumentPage,
});
